import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Users, 
  Instagram, 
  Calendar, 
  Award, 
  Heart, 
  Star,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  TrendingUp,
  Globe,
  Camera,
  Gift,
  Code,
  Zap,
  Target,
  DollarSign,
  UserPlus,
  Briefcase,
  Handshake,
  BookOpen,
  Shield,
  Crown,
  Building2,
  Megaphone,
  ChevronRight,
  ExternalLink,
  Trophy
} from "lucide-react";

const influencerBenefits = [
  {
    icon: Gift,
    title: "Free Premium Access",
    description: "Complimentary SnuggleCrafters subscription for approved creators"
  },
  {
    icon: DollarSign,
    title: "Commission Earnings",
    description: "Earn competitive commission on every successful referral"
  },
  {
    icon: Users,
    title: "Exclusive Community",
    description: "Join our private creator community with support and resources"
  },
  {
    icon: Star,
    title: "Early Access",
    description: "Be first to try new features and seasonal content"
  }
];

const commissionTiers = [
  {
    tier: "Bronze",
    requirement: "1-10 referrals/month",
    commission: "20%",
    color: "from-amber-500 to-orange-500",
    benefits: ["Free subscription", "Basic support", "Monthly resources"]
  },
  {
    tier: "Silver",
    requirement: "11-25 referrals/month",
    commission: "25%",
    color: "from-gray-400 to-slate-500",
    benefits: ["All Bronze benefits", "Priority support", "Custom discount codes"]
  },
  {
    tier: "Gold",
    requirement: "26+ referrals/month",
    commission: "30%",
    color: "from-yellow-400 to-amber-500",
    benefits: ["All Silver benefits", "Dedicated manager", "Exclusive events", "Co-marketing opportunities"]
  }
];

const developerFeatures = [
  {
    icon: Code,
    title: "Story Generation API",
    description: "Generate personalized bedtime stories programmatically"
  },
  {
    icon: Zap,
    title: "Real-time Processing",
    description: "Fast AI-powered story creation with low latency"
  },
  {
    icon: Shield,
    title: "Enterprise Security",
    description: "SOC 2 compliant with robust data protection"
  },
  {
    icon: BookOpen,
    title: "Comprehensive Docs",
    description: "Detailed API documentation with code examples"
  }
];

const apiPlans = [
  {
    name: "Pro",
    price: "$49",
    requests: "10,000",
    period: "per month",
    features: ["Advanced customization", "Priority support", "Higher rate limits", "Analytics dashboard"],
    cta: "Start Free Trial",
    popular: true
  },
  {
    name: "Enterprise",
    price: "Custom",
    requests: "Unlimited",
    period: "",
    features: ["Custom integrations", "Dedicated support", "SLA guarantees", "White-label options"],
    cta: "Contact Sales",
    popular: false
  }
];

const partnershipTypes = [
  {
    icon: Building2,
    title: "Educational Institutions",
    description: "Special programs for schools, libraries, and learning centers",
    benefits: ["Volume discounts", "Curriculum integration", "Training resources"],
    cta: "Learn More"
  },
  {
    icon: Heart,
    title: "Children's Brands",
    description: "Collaborate with toy companies, publishers, and family brands",
    benefits: ["Co-branded content", "Cross-promotion", "Revenue sharing"],
    cta: "Partner With Us"
  },
  {
    icon: Globe,
    title: "Content Platforms",
    description: "Integration opportunities for apps, websites, and platforms",
    benefits: ["API access", "White-label solutions", "Developer resources"],
    cta: "Explore Integration"
  },
  {
    icon: Shield,
    title: "Technical Support",
    description: "Get dedicated technical assistance and developer support",
    benefits: ["24/7 support", "Implementation guidance", "Troubleshooting help"],
    cta: "Technical support"
  },
  {
    icon: Crown,
    title: "Premium Partners",
    description: "Exclusive partnerships for established family entertainment brands",
    benefits: ["Custom development", "Dedicated account management", "Marketing support"],
    cta: "Contact Us"
  }
];

const affiliateFeatures = [
  {
    icon: Target,
    title: "Easy Tracking",
    description: "Comprehensive dashboard to monitor your performance and earnings"
  },
  {
    icon: DollarSign,
    title: "Competitive Rates",
    description: "Industry-leading commission rates with performance bonuses"
  },
  {
    icon: UserPlus,
    title: "Marketing Materials",
    description: "Ready-to-use banners, links, and promotional content"
  },
  {
    icon: CheckCircle2,
    title: "Monthly Payouts",
    description: "Reliable monthly payments with multiple payout options"
  }
];

export default function ProgramsPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Navigation />
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Handshake className="w-4 h-4" />
            Partnership Programs
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Join the SnuggleCrafters Family
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Discover partnership opportunities designed for creators, developers, businesses, and affiliates. 
            Grow your audience, monetize your content, and help families worldwide create magical bedtime stories.
          </p>
        </div>

        {/* Programs Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          
          {/* Influencer Program */}
          <Card className="relative overflow-hidden border-2 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-300 hover:shadow-xl">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500 to-pink-500 rounded-bl-[100px] opacity-10"></div>
            <CardHeader className="relative">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <Megaphone className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <Badge variant="secondary" className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  Most Popular
                </Badge>
              </div>
              <CardTitle className="text-2xl">Influencer Program</CardTitle>
              <CardDescription className="text-base">
                Partner with us as a content creator and earn while sharing the magic of AI-powered bedtime stories with your audience.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Commission Tiers */}
              <div>
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Commission Tiers
                </h4>
                <div className="grid gap-3">
                  {commissionTiers.map((tier, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${tier.color}`}></div>
                        <div>
                          <span className="font-medium">{tier.tier}</span>
                          <span className="text-sm text-muted-foreground ml-2">({tier.requirement})</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits Grid */}
              <div>
                <h4 className="font-semibold text-lg mb-4">Key Benefits</h4>
                <div className="grid grid-cols-2 gap-3">
                  {influencerBenefits.map((benefit, index) => (
                    <div key={index} className="text-center p-3">
                      <benefit.icon className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                      <h5 className="font-medium text-sm mb-1">{benefit.title}</h5>
                      <p className="text-xs text-muted-foreground">{benefit.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="flex gap-3">
                <Link href="/snugglecrafters-influencer-program" className="flex-1">
                  <Button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700" data-testid="button-apply-influencer">
                    Apply Now
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Button variant="outline" size="sm">
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Developer Program */}
          <Card className="relative overflow-hidden border-2 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-300 hover:shadow-xl">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-bl-[100px] opacity-10"></div>
            <CardHeader className="relative">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Code className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  API Access
                </Badge>
              </div>
              <CardTitle className="text-2xl">Developer Program</CardTitle>
              <CardDescription className="text-base">
                Integrate our AI story generation capabilities into your applications with our powerful and easy-to-use API.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* API Plans */}
              <div>
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-500" />
                  API Plans
                </h4>
                <div className="space-y-3">
                  {apiPlans.map((plan, index) => (
                    <div key={index} className={`p-4 rounded-lg border-2 ${plan.popular ? 'border-blue-300 bg-blue-50 dark:bg-blue-950 dark:border-blue-600' : 'border-gray-200 dark:border-gray-700'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-semibold">{plan.name}</h5>
                        {plan.popular && (
                          <Badge className="bg-blue-500">Most Popular</Badge>
                        )}
                      </div>
                      <div className="flex items-baseline gap-1 mb-2">
                        <span className="text-2xl font-bold">{plan.price}</span>
                        {plan.period && <span className="text-sm text-muted-foreground">/{plan.period}</span>}
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {plan.requests} requests {plan.period}
                      </p>
                      <ul className="text-xs space-y-1">
                        {plan.features.slice(0, 2).map((feature, fidx) => (
                          <li key={fidx} className="flex items-center gap-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* Developer Features */}
              <div>
                <h4 className="font-semibold text-lg mb-4">Core Features</h4>
                <div className="grid grid-cols-2 gap-3">
                  {developerFeatures.map((feature, index) => (
                    <div key={index} className="text-center p-3">
                      <feature.icon className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                      <h5 className="font-medium text-sm mb-1">{feature.title}</h5>
                      <p className="text-xs text-muted-foreground">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="flex gap-3">
                <Link href="/developer-portal" className="flex-1">
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700" data-testid="button-join-developer">
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link href="/api-docs">
                  <Button variant="outline" size="sm" data-testid="button-view-docs">
                    <BookOpen className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Partnership Opportunities */}
        <Card className="mb-16">
          <CardHeader className="text-center">
            <div className="inline-flex items-center gap-2 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-4 py-2 rounded-full text-sm font-medium mb-4 mx-auto">
              <Briefcase className="w-4 h-4" />
              Business Partnerships
            </div>
            <CardTitle className="text-3xl">Partnership Opportunities</CardTitle>
            <CardDescription className="text-lg max-w-2xl mx-auto">
              Join forces with SnuggleCrafters to create unique experiences for families and expand your reach in the children's content market.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {partnershipTypes.map((partnership, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-all duration-300 border-2 hover:border-green-300 dark:hover:border-green-600">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <partnership.icon className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{partnership.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{partnership.description}</p>
                    
                    <div className="space-y-2 mb-6">
                      {partnership.benefits.map((benefit, bidx) => (
                        <div key={bidx} className="flex items-center text-xs gap-2">
                          <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" />
                          <span>{benefit}</span>
                        </div>
                      ))}
                    </div>

                    <Link href={`/partnerships/${partnership.title.toLowerCase().replace(/\s+/g, '-')}`}>
                      <Button variant="outline" className="w-full group" data-testid={`button-${partnership.title.toLowerCase().replace(/\s+/g, '-')}`}>
                        {partnership.cta}
                        <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-muted-foreground mb-4">
                Have a unique partnership idea? We'd love to hear from you.
              </p>
              <Link href="/partnerships/contact">
                <Button variant="outline" className="bg-green-50 hover:bg-green-100 dark:bg-green-950 dark:hover:bg-green-900" data-testid="button-contact-partnerships">
                  <Handshake className="w-4 h-4 mr-2" />
                  Contact Partnership Team
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Affiliate Program */}
        <Card className="mb-16">
          <CardHeader className="text-center">
            <div className="inline-flex items-center gap-2 bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 px-4 py-2 rounded-full text-sm font-medium mb-4 mx-auto">
              <DollarSign className="w-4 h-4" />
              Affiliate Marketing
            </div>
            <CardTitle className="text-3xl">Affiliate Program</CardTitle>
            <CardDescription className="text-lg max-w-2xl mx-auto">
              Earn money by referring new customers to SnuggleCrafters. Perfect for bloggers, website owners, and anyone who loves recommending great products.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {affiliateFeatures.map((feature, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950 dark:to-amber-950 rounded-lg p-6 text-center">
              <h3 className="text-2xl font-bold mb-4">Ready to Start Earning?</h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Join thousands of affiliates who are earning commissions by sharing SnuggleCrafters with their networks. 
                No experience required - we provide all the tools and support you need.
              </p>
              <div className="flex gap-4 justify-center">
                <Link href="/affiliate/signup">
                  <Button className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700" data-testid="button-join-affiliate">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Join Affiliate Program
                  </Button>
                </Link>
                <Link href="/affiliate/info">
                  <Button variant="outline" data-testid="button-learn-affiliate">
                    Learn More
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Section */}
        <Card className="text-center bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 border-2 border-purple-200 dark:border-purple-800">
          <CardContent className="pt-8">
            <h2 className="text-2xl font-bold mb-4">Questions About Our Programs?</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Our partnerships team is here to help you find the perfect program for your goals. 
              Get in touch to discuss custom opportunities or learn more about any of our programs.
            </p>
            <div className="flex gap-4 justify-center">
              <Link href="/contact">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700" data-testid="button-contact-team">
                  <Heart className="w-4 h-4 mr-2" />
                  Contact Our Team
                </Button>
              </Link>
              <Link href="/help">
                <Button variant="outline" data-testid="button-visit-help">
                  Visit Help Center
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}